import { computed, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import http from '@/services/http';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';

/**
 * Resolve ONE player's reported `player_data_current_track_code` into the full
 * row shape the track cards render.
 *
 * {@link useCurrentTrackSummaries} already resolves codes for the cards LIST,
 * but it returns a summary — name, artist, album, cover — which is all a
 * one-line card needs. A track card also wants duration, rating, remix, genre
 * and tags, so the inspector reads the whole record for its single code.
 *
 * A code that doesn't resolve (stale check-in, test data) yields null and the
 * caller falls back to showing the raw code.
 */

/** Follow the row live-poll cadence, so now-playing keeps up with check-ins. */
const CURRENT_TRACK_STALE_MS = 30_000;

function readDisplayString(value: unknown): string | null {
    if (value && typeof value === 'object' && 'display_string' in value) {
        const display = (value as { display_string?: unknown }).display_string;
        return typeof display === 'string' && display.trim() !== '' ? display : null;
    }
    return null;
}

function readNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readId(value: unknown): number | null {
    if (value && typeof value === 'object' && 'id' in value) {
        return readNumber((value as { id?: unknown }).id);
    }
    return null;
}

/**
 * @param trackCode the code the player reported
 * @param position  the playlist track number the same check-in reported, shown
 *                  where a playlist card would show its position
 */
export function useCurrentTrackRow(
    trackCode: Ref<string | null>,
    position: Ref<number | null>,
): { track: Ref<PlaylistTrackRow | null> } {
    const normalizedCode = computed(() => trackCode.value?.trim().toUpperCase() ?? null);

    const query = useQuery({
        queryKey: computed(() => ['current-track-row', normalizedCode.value] as const),
        queryFn: async (): Promise<Record<string, unknown> | null> => {
            const response = await http.post('/api/live_track/search', {
                perPage: 1,
                withTotal: false,
                filter: {
                    ruleScopes: {
                        id: 'root',
                        scopeType: 'and',
                        rules: [
                            {
                                id: 'current-track-code',
                                column: 'code',
                                criterion: 'equals',
                                value: normalizedCode.value,
                                field: null,
                            },
                        ],
                    },
                },
            });
            const data = (response.data as { data?: Record<string, unknown>[] }).data ?? [];
            return data[0] ?? null;
        },
        enabled: computed(() => normalizedCode.value !== null),
        staleTime: CURRENT_TRACK_STALE_MS,
        retry: 1,
    });

    const track = computed<PlaylistTrackRow | null>(() => {
        const record = query.data.value;
        if (!record) return null;
        return {
            id: readNumber(record.id) ?? 0,
            code: readString(record.code),
            name: readString(record.name),
            remix_name: readString(record.remix_name),
            artist_id: readId(record.artist),
            artist_name: readDisplayString(record.artist),
            album_id: readId(record.album),
            album_name: readDisplayString(record.album),
            genre_name: readDisplayString(record.genre),
            rating: readString(record.rating),
            playlist_count: readNumber(record.playlist_count),
            isrc: readString(record.isrc),
            bitrate: readNumber(record.bitrate),
            duration: readNumber(record.duration),
            copyright: readNumber(record.copyright),
            cover_art_signed_url: readString(record.cover_art_signed_url),
            uploaded_date: readString(record.uploaded_date),
            created_at: readString(record.created_at),
            // The check-in says which track number of the playlist is playing,
            // which is exactly what a card shows in its position slot.
            position: position.value,
        };
    });

    return { track };
}
